import { TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import React from 'react';
import { useOutletContext } from 'react-router-dom';

import { TabPaper } from '@/components/atoms/TabPaper';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { TIMEZONE_OPTIONS } from '@/data/timezones';
import { UserPreferencesContextType } from '@/DTOs/userPreferences';
import { useTranslator } from '@/hooks/useTranslator';
import { LabelAndValue } from '@/types/labelAndValue';

export const SettingsTimeZoneTab: React.FC = (): React.ReactElement => {
  const context = useOutletContext<UserPreferencesContextType>();
  const { preferences, loading, update } = context;

  const tr = useTranslator();
  const [timezone, setTimezone] = React.useState(preferences.timezone);

  const sortedTimezones = React.useMemo(
    (): readonly LabelAndValue[] =>
      TIMEZONE_OPTIONS.map(
        ({ label, value }: LabelAndValue): LabelAndValue => ({
          label: tr(label),
          value: value,
        }),
      ).sort(({ label: l1 }: LabelAndValue, { label: l2 }: LabelAndValue): number =>
        l1.localeCompare(l2),
      ),
    [tr],
  );

  const handleTimezoneChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setTimezone(event.target.value);
    },
    [],
  );

  const handleSave = React.useCallback(async (): Promise<void> => {
    await update({ timezone: timezone });
  }, [timezone, update]);

  return (
    <TabPaper>
      <Box display="flex" flexDirection="column" gap={5}>
        <Box>
          <Typography variant="h7" component="h6">
            {tr('TIME_ZONE')}
          </Typography>
          <Box width={422} mt={2}>
            <TextField
              label={tr('TIME_ZONE')}
              placeholder={tr('TIME_ZONE')}
              fullWidth={true}
              value={timezone}
              onChange={handleTimezoneChange}
              select={true}>
              {sortedTimezones.map(
                (timezoneOption: LabelAndValue): React.ReactElement => (
                  <MenuItem key={timezoneOption.value} value={timezoneOption.value}>
                    {tr(timezoneOption.label)}
                  </MenuItem>
                ),
              )}
            </TextField>
          </Box>
        </Box>
        <Box>
          <LoadingButton variant="dark" loading={loading} onClick={handleSave}>
            {tr('SAVE_CHANGES')}
          </LoadingButton>
        </Box>
      </Box>
    </TabPaper>
  );
};
